import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { signUp } from "@/lib/auth-client";

const signupSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email("Invalid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SignupValues = z.infer<typeof signupSchema>;

export function SignupForm() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: "", email: "", password: "", confirmPassword: "" },
  });

  async function onSubmit(values: SignupValues) {
    const { error } = await signUp.email({
      name: values.name,
      email: values.email,
      password: values.password,
    });
    if (error) {
      toast.error(error.message ?? "Failed to create account");
      return;
    }
    window.location.href = "/dashboard";
  }

  return (
    <div style={cardStyle}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <h1 style={headingStyle}>Create account</h1>
        <p style={{ fontSize: 13, color: "#608099" }}>Fill in your details to get started</p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        <div>
          <label htmlFor="signup-name" style={labelStyle}>
            Name
          </label>
          <input
            id="signup-name"
            placeholder="Your name"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#4334dc";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
            }}
            {...register("name")}
          />
          {errors.name && <p style={errorStyle}>{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-email" style={labelStyle}>
            Email
          </label>
          <input
            id="signup-email"
            type="email"
            placeholder="you@nymbl.app"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#4334dc";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
            }}
            {...register("email")}
          />
          {errors.email && <p style={errorStyle}>{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-password" style={labelStyle}>
            Password
          </label>
          <input
            id="signup-password"
            type="password"
            placeholder="••••••••"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#4334dc";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
            }}
            {...register("password")}
          />
          {errors.password && <p style={errorStyle}>{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-confirm" style={labelStyle}>
            Confirm Password
          </label>
          <input
            id="signup-confirm"
            type="password"
            placeholder="••••••••"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#4334dc";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
            }}
            {...register("confirmPassword")}
          />
          {errors.confirmPassword && <p style={errorStyle}>{errors.confirmPassword.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          style={isSubmitting ? { ...btnStyle, opacity: 0.6, cursor: "not-allowed" } : btnStyle}
        >
          {isSubmitting ? "Creating account…" : "Create Account"}
        </button>
      </form>

      <p style={{ textAlign: "center", fontSize: 13, color: "#608099", marginTop: 24 }}>
        Already have an account?{" "}
        <a href="/login" style={{ color: "#4334dc", textDecoration: "none", fontWeight: 600 }}>
          Sign in
        </a>
      </p>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #eeeeee",
  borderRadius: 12,
  padding: "36px 32px",
  boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
};

const headingStyle: React.CSSProperties = {
  fontFamily: "'Raleway', sans-serif",
  fontSize: 26,
  fontWeight: 800,
  color: "#0f1944",
  letterSpacing: "-0.02em",
  marginBottom: 6,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#608099",
  marginBottom: 6,
  fontFamily: "'Montserrat', sans-serif",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "11px 14px",
  border: "1.5px solid #e0e0e0",
  borderRadius: 8,
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 14,
  color: "#0f1944",
  background: "#ffffff",
  outline: "none",
  transition: "border-color 0.2s",
  boxSizing: "border-box",
};

const btnStyle: React.CSSProperties = {
  width: "100%",
  padding: "13px",
  background: "#4334dc",
  color: "#ffffff",
  border: "none",
  borderRadius: 8,
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  letterSpacing: "0.02em",
  marginTop: 4,
  transition: "background 0.2s",
};

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#dc2626",
  marginTop: 4,
  fontFamily: "'Montserrat', sans-serif",
};
